import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import SideSheetModal from "@/components/SideSheetModal";
import TaskSideSheetContent from "./TaskSideSheetContent";
import TaskSideSheetSkeleton from "./TaskSideSheetSkeleton";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary/ErrorBoundary";
import { GET_TASK_QUERY } from "./queries";
import { type Path, useNavigate } from "@/router";
import useAmplitude from "@/hooks/useAmplitude";
import { TASK_ANALYTICS_EVENTS, getTaskAnalyticsData } from "@/pages/tasks/_utils/amplitude";
import AddTaskReferenceSideSheet from "@/pages/tasks/[taskId]/_components/AddTaskReferenceSideSheet";
import { TASKS_QUERY } from "@/pages/tasks/queries";
import { TaskStageValues } from "@/types";

export type TaskSideSheetProps = {
  open: boolean;
  onClose: () => void;
  taskId: string | null;
  title?: string;
};

export default function TaskSideSheet({ open, onClose, taskId, title = "Task Details" }: TaskSideSheetProps) {
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const [addReferenceOpen, setAddReferenceOpen] = useState(false);
  const { data, loading, error } = useQuery(GET_TASK_QUERY, {
    variables: { id: taskId },
    fetchPolicy: "cache-first",
    skip: !taskId || !open
  });

  const task = data?.task;
  const existingReferenceIds = useMemo(() => (task?.references || []).map((ref) => ref.id), [task]);
  const canAddReference = task?.stage === TaskStageValues.OPEN;

  useEffect(() => {
    if (!open) setAddReferenceOpen(false);
  }, [open]);

  function setIsSideSheetOpen(isOpen: boolean) {
    if (!isOpen) onClose();
  }

  function handleViewTask() {
    if (taskId) {
      if (task) {
        trackEvent(TASK_ANALYTICS_EVENTS.opened, {
          ...getTaskAnalyticsData(task, "task_side_sheet"),
          openSource: "view_task_button"
        });
      }
      onClose();
      navigate(`/tasks/${taskId}` as Path);
    }
  }

  return (
    <>
      <SideSheetModal isSidebarOpen={open} setSidebarOpen={setIsSideSheetOpen} title={title} actions={null}>
        {loading ? (
          <TaskSideSheetSkeleton />
        ) : error ? (
          <ErrorBoundary message={error.message} variant="inline" />
        ) : task ? (
          <TaskSideSheetContent
            task={task}
            onViewTask={handleViewTask}
            onAddReference={canAddReference ? () => setAddReferenceOpen(true) : undefined}
          />
        ) : (
          <EmptyState iconName="inventory" title="Task Not Found" />
        )}
      </SideSheetModal>
      {taskId && addReferenceOpen ? (
        <AddTaskReferenceSideSheet
          open
          onClose={() => setAddReferenceOpen(false)}
          taskId={taskId}
          existingReferenceIds={existingReferenceIds}
          refetchQueries={[GET_TASK_QUERY, TASKS_QUERY]}
          source="task_side_sheet"
        />
      ) : null}
    </>
  );
}
